import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    Chip,
    Divider,
    Card,
    CardContent,
    Grid,
    Badge,
    Menu,
    MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import ReplyIcon from '@mui/icons-material/Reply';
import FilterListIcon from '@mui/icons-material/FilterList';
import AdminLayout from '../../../components/AdminLayout';
import axios from 'axios';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const AdminMessagesPage = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentMessage, setCurrentMessage] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read', 'replied'
    const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

    useEffect(() => {
        const fetchMessages = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${backendUrl}/contact`);
                setMessages(response.data);
            } catch (error) {
                console.error('Error fetching messages:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load messages. Please try again.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, []);

    const handleOpenReplyDialog = (message) => {
        setCurrentMessage(message);

        // Mark as read when opening
        if (!message.read) {
            toggleReadStatus(message);
        }

        // Default reply template
        setReplyText(
            `Dear ${message.name},\n\nThank you for contacting Done Right Home Services. In response to your inquiry:\n\n\n\nBest regards,\nDone Right Home Services Team`
        );

        setErrors({});
        setReplyDialogOpen(true);
    };

    const handleCloseReplyDialog = () => {
        setReplyDialogOpen(false);
    };

    const handleOpenDeleteDialog = (message) => {
        setCurrentMessage(message);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const handleReplyTextChange = (e) => {
        setReplyText(e.target.value);

        // Clear error when user types
        if (errors.replyText) {
            setErrors({
                ...errors,
                replyText: '',
            });
        }
    };

    const validateReplyForm = () => {
        const newErrors = {};

        if (!replyText.trim()) {
            newErrors.replyText = 'Reply message is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendReply = async () => {
        if (!validateReplyForm()) {
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            console.log(token);
            await axios.post(
                `${backendUrl}/contact/${currentMessage._id}/reply`,
                {
                    replyText,
                },
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                }
            );

            // For now, update locally
            const updatedMessages = messages.map((message) =>
                message._id === currentMessage._id
                    ? { ...message, replied: true, read: true }
                    : message
            );
            setMessages(updatedMessages);

            setSnackbar({
                open: true,
                message: 'Reply sent successfully!',
                severity: 'success',
            });

            handleCloseReplyDialog();
        } catch (error) {
            console.error('Error sending reply:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send reply. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentMessage) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${backendUrl}/contact/${currentMessage._id}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            });

            const filteredMessages = messages.filter(
                (message) => message._id !== currentMessage._id
            );
            setMessages(filteredMessages);

            setSnackbar({
                open: true,
                message: 'Message deleted successfully!',
                severity: 'success',
            });

            handleCloseDeleteDialog();
        } catch (error) {
            console.error('Error deleting message:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete message. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const toggleReadStatus = async (message) => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            await axios.put(
                `${backendUrl}/contact/${message._id}/toggle-read-status`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                            ? `Bearer ${token}`
                            : undefined,
                    },
                }
            );

            // Update locally
            const updatedMessage = { ...message, read: !message.read };
            const updatedMessages = messages.map((item) =>
                item._id === message._id ? updatedMessage : item
            );
            setMessages(updatedMessages);

            const statusText = updatedMessage.read ? 'read' : 'unread';
            setSnackbar({
                open: true,
                message: `Message marked as ${statusText}`,
                severity: 'success',
            });
        } catch (error) {
            console.error('Error toggling message status:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update message. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        });
    };

    const handleOpenFilterMenu = (event) => {
        setFilterMenuAnchor(event.currentTarget);
    };

    const handleCloseFilterMenu = () => {
        setFilterMenuAnchor(null);
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        handleCloseFilterMenu();
    };

    const filteredMessages = messages.filter((message) => {
        if (filter === 'all') return true;
        if (filter === 'unread') return !message.read;
        if (filter === 'read') return message.read && !message.replied;
        if (filter === 'replied') return message.replied;
        return true;
    });

    // Count unread messages
    const unreadCount = messages.filter((message) => !message.read).length;

    return (
        <AdminLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h4" component="h1">
                            Messages
                        </Typography>
                        {unreadCount > 0 && (
                            <Badge
                                badgeContent={unreadCount}
                                color="error"
                                sx={{ ml: 2 }}
                            >
                                <MarkEmailUnreadIcon color="action" />
                            </Badge>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FilterListIcon />}
                        onClick={handleOpenFilterMenu}
                    >
                        {filter === 'all'
                            ? 'All Messages'
                            : filter === 'unread'
                            ? 'Unread'
                            : filter === 'read'
                            ? 'Read'
                            : 'Replied'}
                    </Button>
                    <Menu
                        anchorEl={filterMenuAnchor}
                        open={Boolean(filterMenuAnchor)}
                        onClose={handleCloseFilterMenu}
                    >
                        <MenuItem onClick={() => handleFilterChange('all')}>
                            All Messages
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange('unread')}>
                            Unread
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange('read')}>
                            Read
                        </MenuItem>
                        <MenuItem onClick={() => handleFilterChange('replied')}>
                            Replied
                        </MenuItem>
                    </Menu>
                </Box>

                <Grid container spacing={3}>
                    {loading && messages.length === 0 ? (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 5,
                            }}
                        >
                            <CircularProgress />
                        </Grid>
                    ) : filteredMessages.length === 0 ? (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 5,
                            }}
                        >
                            <Typography variant="body1">
                                No messages found.{' '}
                                {filter !== 'all'
                                    ? 'Try changing the filter.'
                                    : ''}
                            </Typography>
                        </Grid>
                    ) : (
                        filteredMessages.map((message) => (
                            <Grid item xs={12} key={message.id}>
                                <Card
                                    sx={{
                                        position: 'relative',
                                        borderLeft: message.read
                                            ? message.replied
                                                ? '4px solid #4caf50'
                                                : '4px solid #2196f3'
                                            : '4px solid #f44336',
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 16,
                                                right: 16,
                                                display: 'flex',
                                                gap: 1,
                                            }}
                                        >
                                            {!message.read ? (
                                                <Tooltip title="Mark as Read">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            toggleReadStatus(
                                                                message
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <MarkEmailReadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Mark as Unread">
                                                    <IconButton
                                                        color="default"
                                                        onClick={() =>
                                                            toggleReadStatus(
                                                                message
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <MarkEmailUnreadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Reply">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() =>
                                                        handleOpenReplyDialog(
                                                            message
                                                        )
                                                    }
                                                    size="small"
                                                >
                                                    <ReplyIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    color="error"
                                                    onClick={() =>
                                                        handleOpenDeleteDialog(
                                                            message
                                                        )
                                                    }
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="bold"
                                                    >
                                                        From: {message.name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Email: {message.email}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Phone: {message.phone}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="primary"
                                                        sx={{ mt: 1 }}
                                                    >
                                                        {new Date(
                                                            message.date
                                                        ).toLocaleString()}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            display: 'flex',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {!message.read ? (
                                                            <Chip
                                                                label="Unread"
                                                                color="error"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ) : message.replied ? (
                                                            <Chip
                                                                label="Replied"
                                                                color="success"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label="Read"
                                                                color="primary"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    Message:
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
                                                    {message.message}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Reply Dialog */}
                <Dialog
                    open={replyDialogOpen}
                    onClose={handleCloseReplyDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Reply to {currentMessage?.name}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 3, mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Original Message:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap' }}
                            >
                                {currentMessage?.message}
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <TextField
                            fullWidth
                            label="Your Reply"
                            multiline
                            rows={8}
                            value={replyText}
                            onChange={handleReplyTextChange}
                            error={!!errors.replyText}
                            helperText={errors.replyText}
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseReplyDialog}>Cancel</Button>
                        <Button
                            onClick={handleSendReply}
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            startIcon={<ReplyIcon />}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                'Send Reply'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete this message from "
                            {currentMessage?.name}"? This action cannot be
                            undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </AdminLayout>
    );
};

export default AdminMessagesPage;
